// frontend/src/components/mice/MoveMouseModal.js
// Slide-out panel body for moving mice into a cage.
//
// Serves BOTH the single-mouse "move" flow and the "bulkMove" flow. Those two
// cases in MiceManagement's renderSlideOutContent were near-identical — same
// cage-search combobox, same inline new-cage form, same validation footer —
// differing only in the heading, the submit handler, the selected-cage state
// pair and the button labels. Those differences are props here.
//
// All state lives in MiceManagement; this component is presentation + wiring.

import React from 'react';

function MoveMouseModal({
  title,
  fieldLabel = 'Select New Cage:',
  cages,
  // combobox
  cageSearchRef,
  cageSearchQuery,
  setCageSearchQuery,
  showCageDropdown,
  setShowCageDropdown,
  selectedCageId,
  setSelectedCageId,
  // inline new-cage form
  showNewCageForm,
  setShowNewCageForm,
  newCage,
  setNewCage,
  onCreateCage,
  isCreatingCage,
  // submit / cancel
  onSubmit,
  isSubmitting,
  submitLabel,
  submittingLabel,
  onCancel,
  // feedback
  error,
}) {
  const query = cageSearchQuery.toLowerCase();
  const filteredCages = cages.filter(
    (cage) =>
      cage.display_cage_number?.toLowerCase().includes(query) ||
      cage.location?.toLowerCase().includes(query)
  );

  const cageLabel = (cage) =>
    `${cage.display_cage_number} - ${cage.location} (${Number(cage.mouse_count) || 0}/${cage.capacity})`;

  return (
    <div className="add-mouse-section">
      <h2>{title}</h2>
      <form onSubmit={onSubmit} className="add-mouse-form">
        <div className="form-group">
          <label>{fieldLabel}</label>
          {/* Combobox wrapper — ref lets the click-outside handler know what's "inside" */}
          <div ref={cageSearchRef} style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Search cage number or location..."
              value={cageSearchQuery}
              onChange={(e) => {
                setCageSearchQuery(e.target.value);
                setSelectedCageId(''); // clear selection while retyping
                setShowCageDropdown(true);
              }}
              onFocus={() => setShowCageDropdown(true)}
              autoComplete="off"
            />
            {showCageDropdown && (
              <ul className="cage-search-dropdown">
                {filteredCages.length > 0 ? (
                  filteredCages.map((cage) => (
                    <li
                      key={cage.id}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setSelectedCageId(cage.id);
                        setCageSearchQuery(cageLabel(cage));
                        setShowCageDropdown(false);
                      }}
                      className={cage.id === selectedCageId ? 'selected' : ''}
                    >
                      {cageLabel(cage)}
                    </li>
                  ))
                ) : (
                  <li className="empty">No cages found</li>
                )}
              </ul>
            )}
          </div>
        </div>

        {!showNewCageForm ? (
          <button
            type="button"
            onClick={() => setShowNewCageForm(true)}
            className="secondary-button"
            style={{ marginBottom: '15px' }}
          >
            New Cage
          </button>
        ) : (
          <div style={{ border: '1px solid #007bff', padding: '10px', borderRadius: '4px', marginBottom: '15px' }}>
            <h4 style={{ margin: '0 0 10px 0' }}>Create New Cage</h4>
            <input
              type="text"
              placeholder="Cage number"
              value={newCage.display_cage_number}
              onChange={(e) => setNewCage({ ...newCage, display_cage_number: e.target.value })}
              style={{ marginBottom: '10px' }}
              required
            />
            <input
              type="text"
              placeholder="Location"
              value={newCage.location}
              onChange={(e) => setNewCage({ ...newCage, location: e.target.value })}
              style={{ marginBottom: '10px' }}
            />
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="button"
                onClick={onCreateCage}
                className="primary-button"
                style={{ flex: 1 }}
                disabled={isCreatingCage}
              >
                {isCreatingCage ? 'Creating...' : 'Create'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowNewCageForm(false);
                  setNewCage({ display_cage_number: '', location: '' });
                }}
                className="secondary-button"
                style={{ flex: 1 }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {error && <div className="error">{error}</div>}

        <div style={{ display: 'flex', gap: '10px' }}>
          <button type="submit" className="primary-button" style={{ flex: 1 }} disabled={isSubmitting}>
            {isSubmitting ? submittingLabel : submitLabel}
          </button>
          <button type="button" onClick={onCancel} className="secondary-button" style={{ flex: 1 }}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default MoveMouseModal;
